import { forwardRef } from "react";
import type { InputHTMLAttributes, SelectHTMLAttributes, TextareaHTMLAttributes } from "react";
import type { LucideIcon } from "lucide-react";
import { cn } from "@/lib/utils";

const BASE =
  "w-full rounded-xl border bg-gray-50 px-4 py-3 text-sm font-medium text-gray-700 outline-none transition-all placeholder:text-gray-400 focus:ring-4 focus:ring-indigo-50";

const STATE = {
  default: "border-gray-100 focus:border-indigo-200",
  error: "border-rose-300 focus:border-rose-400 focus:ring-rose-50",
};

interface InputProps extends InputHTMLAttributes<HTMLInputElement> {
  invalid?: boolean;
  icon?: LucideIcon;
}

export const Input = forwardRef<HTMLInputElement, InputProps>(function Input(
  { className, invalid, icon: Icon, ...props },
  ref,
) {
  const input = (
    <input
      ref={ref}
      className={cn(BASE, invalid ? STATE.error : STATE.default, Icon && "pl-11", className)}
      {...props}
    />
  );
  if (!Icon) return input;
  return (
    <div className="relative">
      <Icon
        size={16}
        className="pointer-events-none absolute left-4 top-1/2 -translate-y-1/2 text-gray-400"
      />
      {input}
    </div>
  );
});

interface SelectProps extends SelectHTMLAttributes<HTMLSelectElement> {
  invalid?: boolean;
}

export const Select = forwardRef<HTMLSelectElement, SelectProps>(function Select(
  { className, invalid, children, ...props },
  ref,
) {
  return (
    <select
      ref={ref}
      className={cn(BASE, invalid ? STATE.error : STATE.default, className)}
      {...props}
    >
      {children}
    </select>
  );
});

interface TextareaProps extends TextareaHTMLAttributes<HTMLTextAreaElement> {
  invalid?: boolean;
}

export const Textarea = forwardRef<HTMLTextAreaElement, TextareaProps>(function Textarea(
  { className, invalid, ...props },
  ref,
) {
  return (
    <textarea
      ref={ref}
      className={cn(BASE, "resize-none", invalid ? STATE.error : STATE.default, className)}
      {...props}
    />
  );
});
